// Dossiers panel on the creator dashboard — the dossiers (drops) this
// creator has been revealed from, with per-drop reveal + holder counts.
// Wired at Epic 3 (the reveal mechanic that populates `shares`); replaces
// the Story-1.26 <PlaceholderPanel> stub.

import { Badge, Card, CardContent } from "@greenhold/components";

import type { DossiersPanelData, DropStatus } from "./useCreatorDashboard";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function dropStatusVariant(
  status: DropStatus,
): "success" | "warning" | "secondary" {
  switch (status) {
    case "live":
      return "success";
    case "retiring":
      return "warning";
    default:
      // scheduled | retired — neutral.
      return "secondary";
  }
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-brand-forest-200 bg-brand-paper-50 px-4 py-3 min-w-[7rem]">
      <p className="text-2xl font-heading font-bold tabular-nums">
        {value.toLocaleString()}
      </p>
      <p className="text-xs uppercase tracking-wider opacity-60">{label}</p>
    </div>
  );
}

export function DossiersPanel({ data }: { data: DossiersPanelData }) {
  if (data.dossiers.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center space-y-2">
          <p className="font-semibold">No dossier appearances yet</p>
          <p className="opacity-80 max-w-xl mx-auto leading-relaxed">
            When collectors open dossiers and reveal a certificate of you, every
            dossier you appear in shows up here — with how many times you&apos;ve
            been pulled and how many collectors hold you.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <SummaryStat label="Dossiers" value={data.dossiers.length} />
        <SummaryStat label="Total opens" value={data.total_opens} />
        <SummaryStat label="Collectors" value={data.total_holders} />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-paper-100 text-left">
              <tr>
                <th className="px-4 py-2">Dossier</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Opens</th>
                <th className="px-4 py-2 text-right">Collectors</th>
                <th className="px-4 py-2">Featured since</th>
              </tr>
            </thead>
            <tbody>
              {data.dossiers.map((d) => (
                <tr
                  key={d.drop_id}
                  className="border-t border-brand-paper-200"
                >
                  <td className="px-4 py-2 font-medium">
                    {d.portfolio_title}
                    {d.portfolio_theme ? (
                      <span className="opacity-60"> · {d.portfolio_theme}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={dropStatusVariant(d.drop_status)}>
                      {d.drop_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {d.opens_count.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {d.holder_count.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 opacity-80">
                    {formatDate(d.first_appeared_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
